// Phase 2: レッスン内容マスター（LessonContent）の CRUD ペイン
// 既存サイトの dark + glass テイストに揃えたデザイン版。
// 視覚スタイルは common.scss の body.lesson-v2 .lesson-content-panel に集約。

import React, { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import useIsMobile from "../../../../hooks/useIsMobile";
import {
  createLessonContent,
  updateLessonContent,
  deleteLessonContent,
} from "../../../../utils/lessonV2Api";

const emptyDraft = {
  name: "",
  target: "",
  color: "",
  description: "",
  instructor_id: "",
  start_time: "",
  end_time: "",
  young_flg: false,
  display_order: 0,
};

const LessonContentPanel = ({ contents, instructors = [], onChanged, busy, setBusy }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState("");
  const isMobile = useIsMobile();
  const [pickerOpen, setPickerOpen] = useState(false);

  const startNew = () => {
    setEditingId(null);
    setDraft(emptyDraft);
    setError("");
  };

  const startEdit = (content) => {
    setEditingId(content.id);
    setDraft({
      name: content.name || "",
      target: content.target || "",
      color: content.color || "",
      description: content.description || "",
      instructor_id: content.instructor_id || "",
      start_time: content.start_time || "",
      end_time: content.end_time || "",
      young_flg: !!content.young_flg,
      display_order: content.display_order || 0,
    });
    setError("");
  };

  const handleField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) {
      setError("レッスン内容名は必須です");
      return;
    }
    if (!draft.start_time || !draft.end_time) {
      setError("開始時刻と終了時刻は必須です");
      return;
    }
    if (draft.start_time >= draft.end_time) {
      setError("開始時刻は終了時刻より前にしてください");
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (editingId) {
        await updateLessonContent(editingId, draft);
      } else {
        await createLessonContent(draft);
      }
      await onChanged();
      startNew();
    } catch (err) {
      setError(err.message || "保存に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (content) => {
    if (
      !window.confirm(
        `「${content.name}」を削除します。よろしいですか？\n（配置で使用中の場合は削除できません）`
      )
    ) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      await deleteLessonContent(content.id);
      await onChanged();
      if (editingId === content.id) startNew();
    } catch (err) {
      if (err.status === 409) {
        setError(err.message || "使用中のため削除できません");
      } else {
        setError(err.message || "削除に失敗しました");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lesson-content-panel">
      {/* セクション: 登録済み一覧（見出しなし） */}
      <section className="lcp-section">
        <div className="lcp-list">
          {!isMobile && (
            <div className="lcp-list__header">
              <span>名前</span>
              <span>対象</span>
              <span>講師</span>
              <span>時間</span>
              <span>色</span>
              <span>操作</span>
            </div>
          )}
          <div className="lcp-list__body">
            {contents.length === 0 ? (
              <div className="lcp-list__empty">未登録</div>
            ) : isMobile ? (
              contents.map((c) => (
                <div
                  key={c.id}
                  className={`lcp-card${editingId === c.id ? " is-editing" : ""}`}
                >
                  <div className="lcp-card__head">
                    <span
                      className="lcp-color-chip"
                      style={{ backgroundColor: c.color || "transparent" }}
                    />
                    <span className="lcp-card__name">{c.name}</span>
                    {c.target ? (
                      <span className="lcp-card__target">（{c.target}）</span>
                    ) : null}
                    {/* 編集/削除はレッスン名と同じ行（右寄せ） */}
                    <div className="lcp-actions">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => startEdit(c)}
                        className="lcp-actions__edit"
                        title="編集"
                        aria-label="編集"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => handleDelete(c)}
                        className="lcp-actions__delete"
                        title="削除"
                        aria-label="削除"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                  <div className="lcp-card__meta">
                    <span>{c.instructor_name || "（講師未設定）"}</span>
                    {c.start_time && c.end_time ? (
                      <span className="lcp-card__time">
                        {c.start_time}-{c.end_time}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              contents.map((c) => (
                <div
                  key={c.id}
                  className={`lcp-list__row${editingId === c.id ? " is-editing" : ""}`}
                >
                  <span className="lcp-list__cell lcp-list__cell--name">
                    {c.name}
                  </span>
                  <span className="lcp-list__cell">{c.target || "—"}</span>
                  <span className="lcp-list__cell">
                    {c.instructor_name || "—"}
                  </span>
                  <span className="lcp-list__cell">
                    {c.start_time && c.end_time
                      ? `${c.start_time}-${c.end_time}`
                      : "—"}
                  </span>
                  <span className="lcp-list__cell lcp-list__cell--color">
                    <span
                      className="lcp-color-chip"
                      style={{ backgroundColor: c.color || "transparent" }}
                    />
                    <code>{c.color || "—"}</code>
                  </span>
                  <span className="lcp-list__cell lcp-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => startEdit(c)}
                      className="lcp-actions__edit"
                      title="編集"
                      aria-label="編集"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleDelete(c)}
                      className="lcp-actions__delete"
                      title="削除"
                      aria-label="削除"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* セクション: 編集フォーム（見出しなし、編集中バッジのみ） */}
      <section className="lcp-section">
        <form className="lcp-form" onSubmit={handleSubmit}>
          {editingId && (
            <div className="lcp-form__editing-tag">編集中 #{editingId}</div>
          )}
          <div className="lcp-form__row">
            <label className="lcp-form__label">名前 *</label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => handleField("name", e.target.value)}
              disabled={busy}
              placeholder="例: HIPHOP / LOCK / DANCE"
              className="lcp-form__input"
            />
          </div>

          <div className="lcp-form__row">
            <label className="lcp-form__label">対象</label>
            <input
              type="text"
              value={draft.target}
              onChange={(e) => handleField("target", e.target.value)}
              disabled={busy}
              placeholder="例: 入門🔰、初級、中級、小学生"
              className="lcp-form__input"
            />
          </div>

          <div className="lcp-form__row">
            <label className="lcp-form__label">講師</label>
            <select
              value={draft.instructor_id || ""}
              onChange={(e) => handleField("instructor_id", e.target.value)}
              disabled={busy}
              className="lcp-form__input"
            >
              <option value="">インストラクターを選択</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="lcp-form__row">
            <label className="lcp-form__label">時間 *</label>
            <div className="lcp-form__time">
              <input
                type="time"
                step="300"
                value={draft.start_time || ""}
                onChange={(e) => handleField("start_time", e.target.value)}
                disabled={busy}
                className="lcp-form__input"
              />
              <span className="lcp-form__time-sep">-</span>
              <input
                type="time"
                step="300"
                value={draft.end_time || ""}
                onChange={(e) => handleField("end_time", e.target.value)}
                disabled={busy}
                className="lcp-form__input"
              />
            </div>
          </div>

          <div className="lcp-form__row">
            <label className="lcp-form__label">色</label>
            <div className="lcp-form__color">
              <div
                className="lcp-form__color-preview"
                style={{ backgroundColor: draft.color || "#ffffff" }}
                onClick={() => !busy && setPickerOpen((v) => !v)}
              />
              <input
                type="text"
                value={draft.color}
                onChange={(e) => handleField("color", e.target.value)}
                disabled={busy}
                placeholder="カラーコード（例：#60dfff）"
                className="lcp-form__input"
              />
            </div>
          </div>

          {pickerOpen && (
            <div className="color-picker-container">
              <div
                className="color-picker-overlay"
                onClick={() => setPickerOpen(false)}
              />
              <div className="color-picker-popup">
                <HexColorPicker
                  color={draft.color || "#ffffff"}
                  onChange={(color) => handleField("color", color)}
                />
                <div className="color-picker-hex">
                  HEX: {draft.color || "#ffffff"}
                </div>
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="color-picker-close"
                >
                  完了
                </button>
              </div>
            </div>
          )}

          <div className="lcp-form__row">
            <label className="lcp-form__label">幼少クラス</label>
            <label className="lcp-form__check">
              <input
                type="checkbox"
                checked={!!draft.young_flg}
                onChange={(e) => handleField("young_flg", e.target.checked)}
                disabled={busy}
              />
              <span>このクラスは幼少対象</span>
            </label>
          </div>

          <div className="lcp-form__row">
            <label className="lcp-form__label">説明</label>
            <textarea
              value={draft.description || ""}
              onChange={(e) => handleField("description", e.target.value)}
              disabled={busy}
              placeholder="任意（メモ）"
              className="lcp-form__input lcp-form__textarea"
            />
          </div>

          {error && <p className="lcp-form__error">{error}</p>}

          <div className="lesson-content-form-actions">
            <button type="submit" disabled={busy} className="submit-btn">
              {editingId ? "更新" : "作成"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={startNew}
                disabled={busy}
                className="cancel-btn"
              >
                新規モードへ
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
};

export default LessonContentPanel;
